import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Icon } from "@/lib/icons";
import { Markdown } from "@/lib/markdown";
import { ProgressRing } from "@/components/ProgressRing";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Crown, Loader2, Clock, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PathDetail() {
  const { pathId } = useParams();
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [path, setPath] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [percent, setPercent] = useState(0);
  const [open, setOpen] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeLesson, setActiveLesson] = useState(null);

  const load = () => api.get(`/paths/${pathId}`).then((r) => { setPath(r.data); setCompleted(r.data.completed || []); setPercent(r.data.percent); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pathId]);

  const locked = path?.level === "Advanced" && !isPro;

  const openLesson = async (lessonTitle) => {
    if (locked) { toast.error("Advanced learning paths require ECO Pro."); return; }
    setActiveLesson(lessonTitle);
    setOpen(true); setLoading(true); setLesson(null);
    try {
      const { data } = await api.post("/paths/lesson", { path_id: pathId, lesson: lessonTitle });
      setLesson(data);
    } catch (e) {
      if (e.response?.status === 402) { toast.error("Advanced learning paths require ECO Pro."); setOpen(false); }
      else toast.error("Could not load lesson");
    } finally { setLoading(false); }
  };

  const markComplete = async () => {
    try {
      const { data } = await api.post("/paths/complete-lesson", { path_id: pathId, lesson: activeLesson });
      setCompleted(data.completed); setPercent(data.percent);
      toast.success("Lesson complete! 🎉");
    } catch (e) {
      if (e.response?.status === 402) toast.error("Advanced learning paths require ECO Pro.");
    }
  };

  if (!path) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-600" /></div>;

  return (
    <div className="space-y-8" data-testid="path-detail-page">
      <Link to="/university" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Founder University</Link>

      <div className="eco-card p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
        <ProgressRing value={percent} size={80} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Icon name={path.icon} size={22} className="text-[#4d8bff]" />
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">{path.title}</h1>
            {path.level === "Advanced" && <Crown size={16} className="text-amber-400" />}
          </div>
          <p className="text-slate-400 mt-2">{path.description}</p>
          <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
            <span className="eco-tag">{path.level}</span>
            <span className="flex items-center gap-1"><Clock size={12} /> ~{path.estimatedHours}h</span>
            <span>{completed.length}/{path.total_lessons} complete</span>
          </div>
        </div>
      </div>

      {locked && (
        <div className="eco-card p-5 flex items-center gap-3 border-amber-400/20" data-testid="path-locked">
          <Crown size={18} className="text-amber-400" />
          <p className="text-sm text-slate-300 flex-1">This is an advanced path. Upgrade to ECO Pro to read lessons and track progress.</p>
          <Link to="/pricing" className="btn-accent text-sm">Upgrade</Link>
        </div>
      )}

      <div className="space-y-6">
        {path.modules.map((mod, mi) => (
          <div key={mi} data-testid={`module-${mi}`}>
            <h2 className="font-display text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="font-mono-eco text-xs text-slate-600">{String(mi + 1).padStart(2, "0")}</span> {mod.title}
            </h2>
            <div className="space-y-2">
              {mod.lessons.map((lessonTitle) => {
                const done = completed.includes(lessonTitle);
                return (
                  <button key={lessonTitle} data-testid={`lesson-${lessonTitle}`} onClick={() => openLesson(lessonTitle)} className="eco-card eco-card-hover w-full p-4 flex items-center gap-3 text-left group">
                    {done ? <CheckCircle2 size={20} className="text-emerald-400 shrink-0" /> : <Circle size={20} className="text-slate-600 shrink-0" />}
                    <span className={`flex-1 ${done ? "text-slate-400" : "text-white"}`}>{lessonTitle}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"><BookOpen size={13} /> Read</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{activeLesson || "Lesson"}</DialogTitle></DialogHeader>
          {loading ? (
            <div className="flex flex-col items-center py-16 gap-3"><Loader2 className="animate-spin text-[#4d8bff]" /><p className="text-sm text-slate-500">ECO is preparing your lesson…</p></div>
          ) : lesson ? (
            <>
              <Markdown>{lesson.content}</Markdown>
              <button data-testid="path-lesson-complete-btn" onClick={markComplete} disabled={completed.includes(activeLesson)} className="btn-accent text-sm mt-2">
                <CheckCircle2 size={16} /> {completed.includes(activeLesson) ? "Completed" : "Mark complete"}
              </button>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
